import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { Box, Grid, Stack, Typography } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';

import { FORM_LABELS, NAMES, HEX_COLORS } from '../../store/constants';
import { IntervalFormatter, IntervalValidation } from '../../utils';
import { Alert, CleanButton, PrimaryButton } from '../default';
import { FormInputPercentage, FormSelect, FormSelectColor } from '../forms/formInputs';
import { FormValidationCreateConsumpionRange } from '../forms/formValidations';

const actions = [
    { name: NAMES.KEEP_LIMIT, label: FORM_LABELS.KEEP_LIMIT },
    { name: NAMES.RAISE_LIMIT, label: FORM_LABELS.RAISE_LIMIT },
    { name: NAMES.REDUCE_LIMIT, label: FORM_LABELS.REDUCE_LIMIT },
    { name: NAMES.ZERO_LIMIT, label: FORM_LABELS.ZERO_LIMIT }
]

const operators = [
    { name: NAMES.GREATER_OR_EQUAL, label: FORM_LABELS.GREATER_OR_EQUAL },
    { name: NAMES.LESSER_OR_EQUAL, label: FORM_LABELS.LESSER_OR_EQUAL },
    { name: NAMES.BETWEEN, label: FORM_LABELS.BETWEEN },
    { name: NAMES.NONE, label: FORM_LABELS.NONE }
]

export const ConsumptionRangeForm = ({ intervalList, setIntervalList, intervalError, setIntervalError }) => {

    const [hideMinField, setHideMinField] = useState(true)
    const [hideMaxField, setHideMaxField] = useState(true)
    const [hidePercentageField, setHidePercentageField] = useState(true)

    const defaultValues = {
        action: '',
        operator: '',
        percentage: '',
        minValue: '',
        maxValue: '',
        color: ''
    }

    const { control, watch, handleSubmit, reset, setValue, clearErrors, formState: { errors } } = useForm({
        defaultValues,
        resolver: yupResolver(FormValidationCreateConsumpionRange),
        context: { hideMinField, hideMaxField, hidePercentageField }
    })

    const selectedOperator = watch('operator')
    const selectedAction = watch('action')

    useEffect(() => {

        const actionsThatHidePercentage = [FORM_LABELS.KEEP_LIMIT, FORM_LABELS.ZERO_LIMIT, '']

        setHidePercentageField(actionsThatHidePercentage.includes(selectedAction))

        if (actionsThatHidePercentage.includes(selectedAction)) {
            setValue('percentage', '')
        }

        clearErrors('percentage')

    }, [selectedAction, setValue, clearErrors])

    useEffect(() => {

        switch (selectedOperator) {
            case FORM_LABELS.GREATER_OR_EQUAL:
                setHideMinField(false)
                setHideMaxField(true)
                setValue('maxValue', '')
                break
            case FORM_LABELS.LESSER_OR_EQUAL:
                setHideMinField(true)
                setHideMaxField(false)
                setValue('minValue', '')
                break
            case FORM_LABELS.BETWEEN:
                setHideMinField(false)
                setHideMaxField(false)
                break
            case FORM_LABELS.NONE:
            default:
                setHideMinField(true)
                setHideMaxField(true)
                setValue('minValue', '')
                setValue('maxValue', '')
                break
        }

        clearErrors(['minValue', 'maxValue'])

    }, [selectedOperator, setValue, clearErrors])

    const onSubmit = handleSubmit(range => {

        const newInterval = {
            ...range,
            consumptionRangeId: uuidv4()
        }

        const formattedInterval = IntervalFormatter.visualizationIntervalFormatter(newInterval)

        const hasConflict = IntervalValidation.validateIntervalConflict(formattedInterval, intervalList)
        const isValid = IntervalValidation.validateNoneOperatorOnSubmit(formattedInterval, intervalList)

        const canSubmitRange = (!hasConflict.state && isValid)

        if (canSubmitRange) {

            const finalInterval = {
                ...formattedInterval,
                operatorLabel: operators.find(operator => operator.label === formattedInterval.operator).name,
                actionLabel: actions.find(action => action.label === formattedInterval.action).name
            }

            setIntervalList(prev => [...prev, finalInterval])
            setIntervalError({ state: false, message: '' })
            reset()

        } else {
            setIntervalError({
                state: true,
                message: isValid
                    ? `Conflito com intervalo existente: ${hasConflict.interval.minValue} - ${hasConflict.interval.maxValue}`
                    : 'Opção "Não importa" não pode coexistir com outras faixas.'
            })
        }
    })

    const hasErrors = Object.keys(errors).length > 0

    return (
        <Stack gap={3}>

            <Stack>
                <Typography fontWeight="bold">Perfil de Consumo e Ação</Typography>
                <Typography variant="body2">
                    Crie perfis de consumo de limite de crédito entre 0 e 100% e determine ações personalizadas.
                </Typography>
            </Stack>

            <Box component='form' onSubmit={onSubmit} noValidate>
                <Grid container spacing={2} alignItems={hasErrors ? 'flex-start' : 'center'}>

                    <Grid item xs={12} md={4}>
                        <FormSelect
                            label={'Consumo'}
                            name={'operator'}
                            itemId={'label'}
                            items={operators}
                            control={control}
                            errors={errors.operator}
                            fullWidth
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormInputPercentage
                            label={'Mínimo'}
                            name={'minValue'}
                            control={control}
                            disabled={hideMinField}
                            errors={errors.minValue}
                            fullWidth
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormInputPercentage
                            label={'Máximo'}
                            name={'maxValue'}
                            control={control}
                            disabled={hideMaxField}
                            errors={errors.maxValue}
                            fullWidth
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={3} xl={4}>
                        <FormSelect
                            label={'Ação'}
                            name={'action'}
                            itemId={'label'}
                            items={actions}
                            control={control}
                            errors={errors.action}
                            fullWidth
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={3} xl={4}>
                        <FormInputPercentage
                            label={'Percentual'}
                            name={'percentage'}
                            control={control}
                            disabled={hidePercentageField}
                            errors={errors.percentage}
                            fullWidth
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={3} xl={2}>
                        <FormSelectColor
                            label={'Cor'}
                            name={'color'}
                            itemId={'value'}
                            items={HEX_COLORS}
                            control={control}
                            errors={errors.color}
                            fullWidth
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={3} xl={2}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <PrimaryButton
                                label='Adicionar'
                                onClick={onSubmit}
                                fullWidth
                            />
                            <CleanButton
                                label='Limpar'
                                onClick={() => reset()}
                                fullWidth
                            />
                        </Stack>
                    </Grid>
                </Grid>
            </Box>

            {intervalError.state && (
                <Alert severity='error' message={intervalError.message} />
            )}

        </Stack>
    )
}

ConsumptionRangeForm.propTypes = {
    intervalList: PropTypes.array.isRequired,
    setIntervalList: PropTypes.func.isRequired,
    intervalError: PropTypes.object.isRequired,
    setIntervalError: PropTypes.func.isRequired
}
